/**
 * Lease Amendment API
 * Client functions for the post-signing lease amendment (addendum) workflow —
 * DC-3 (revived) + DC-16..23. Mirrors signing.api.ts's LEASE GENERATION API section's
 * fetch-wrapper shape (bare apiFetch calls, no wrapping envelope beyond what the route
 * itself returns).
 *
 * Response types are DERIVED from the endpoint's Zod response schema imported from
 * `@resida/api-types/lease-generation` (z.infer), the way apps/landlord/src/types/lease-generation.ts
 * derives its types from lease-generation.ts's schemas — see
 * packages/api-types/src/lease-generation/lease-amendment.ts, which is the schemas' shared
 * owner (apps/api/src/routes/manager/lease-amendment.schemas.ts re-exports them for route
 * registration).
 */

import { z } from 'zod';
import {
  LeaseAmendmentStatusSchema,
  LeaseAmendmentChangeSchema,
  LeaseAmendmentResponseSchema,
  LeaseAmendmentDetailResponseSchema,
  AvailableAddendumResponseSchema,
  EffectiveAddendumResponseSchema,
  EffectiveAddendumSetResponseSchema,
  type ResendResponse,
} from '@resida/api-types/lease-generation';
import { apiFetch, ApiClientError } from './api.core';

// =====================================================
// STAGED CHANGE
// =====================================================

export type LeaseAmendmentChange = z.infer<typeof LeaseAmendmentChangeSchema>;

// =====================================================
// ADDENDUM CATALOG / EFFECTIVE SET
// =====================================================

export type AvailableAddendum = z.infer<typeof AvailableAddendumResponseSchema>;
export type AvailableAddendumVariable = AvailableAddendum['variables'][number];

export type EffectiveAddendum = z.infer<typeof EffectiveAddendumResponseSchema>;

/** DC-23: effective set plus the data recorded on the base doc for slugs NOT in it. */
export type EffectiveAddendumSet = z.infer<typeof EffectiveAddendumSetResponseSchema>;

/** Free-form (landlord-authored) addendum slug: `custom:<uuid>` (BR-LA-14). */
export const CUSTOM_ADDENDUM_SLUG_PREFIX = 'custom:';
export function isCustomAddendumSlug(slug: string): boolean {
  return slug.startsWith(CUSTOM_ADDENDUM_SLUG_PREFIX);
}

/**
 * A 422 `{ validationErrors: [{ code, message }] }` rejection from update/generate
 * (INVALID_ADDENDUM_VARIABLES / CUSTOM_ADDENDUM_INVALID / ANCHOR_IN_EDITED_CONTENT).
 * `code` is the first error's code; `message` joins every message for inline display.
 */
export class AmendmentValidationError extends Error {
  constructor(
    message: string,
    public code: string,
    public validationErrors: Array<{ code: string; message: string }>
  ) {
    super(message);
    this.name = 'AmendmentValidationError';
  }
}

/**
 * apiFetch does not surface a bare `{ validationErrors }` 422 body (it has no `message`), so on
 * a generic 422 re-read the body: a validation failure is side-effect free, so repeating the
 * request only to read its rejection is safe.
 */
async function withValidationErrors<T>(
  endpoint: string,
  options: RequestInit,
  run: () => Promise<T>
): Promise<T> {
  try {
    return await run();
  } catch (err) {
    if (!(err instanceof ApiClientError) || err.status !== 422) throw err;
    let body: unknown;
    try {
      const headers: Record<string, string> = options.body ? { 'Content-Type': 'application/json' } : {};
      const res = await fetch(endpoint, { ...options, credentials: 'include', headers });
      body = await res.json();
    } catch {
      throw err;
    }
    const list = (body as { validationErrors?: Array<{ code: string; message: string }> } | null)
      ?.validationErrors;
    if (!Array.isArray(list) || list.length === 0) throw err;
    throw new AmendmentValidationError(list.map(e => e.message).join(' '), list[0]!.code, list);
  }
}

// =====================================================
// AMENDMENT RESPONSES
// =====================================================

export type LeaseAmendmentStatus = z.infer<typeof LeaseAmendmentStatusSchema>;

export type LeaseAmendmentResponse = z.infer<typeof LeaseAmendmentResponseSchema>;

export type LeaseAmendmentDetailResponse = z.infer<typeof LeaseAmendmentDetailResponseSchema>;

export interface UpdateAmendmentParams {
  changes: LeaseAmendmentChange[];
}

// =====================================================
// LEASE AMENDMENT API (DC-3 revived, DC-16..23)
// =====================================================

/**
 * List every addendum available in the addendum catalog (DC-3 revived, BR-LA-13).
 */
export function listAvailableAddendums(): Promise<AvailableAddendum[]> {
  return apiFetch<AvailableAddendum[]>('/landlord/api/lease-generation/addendums');
}

/**
 * Compute a lease's current effective addendum set (DC-23, BR-LA-06).
 */
export function getEffectiveAddendums(leaseId: string): Promise<EffectiveAddendumSet> {
  return apiFetch<EffectiveAddendumSet>(
    `/landlord/api/leases/${leaseId}/effective-addendums`
  );
}

/**
 * List every amendment (any status) for a lease, ordered by sequence number (DC-16).
 */
export function listAmendments(leaseId: string): Promise<LeaseAmendmentResponse[]> {
  return apiFetch<LeaseAmendmentResponse[]>(`/landlord/api/leases/${leaseId}/amendments`);
}

/**
 * Get a single amendment with its assembled content (DC-17).
 */
export function getAmendment(
  leaseId: string,
  amendmentId: string
): Promise<LeaseAmendmentDetailResponse> {
  return apiFetch<LeaseAmendmentDetailResponse>(
    `/landlord/api/leases/${leaseId}/amendments/${amendmentId}`
  );
}

/**
 * Start a new DRAFT amendment for a lease, seeded from its current effective set (DC-18).
 */
export function startAmendment(leaseId: string): Promise<LeaseAmendmentResponse> {
  return apiFetch<LeaseAmendmentResponse>(`/landlord/api/leases/${leaseId}/amendments`, {
    method: 'POST',
  });
}

/**
 * Persist the staged change set on a DRAFT amendment (DC-19).
 */
export function updateAmendment(
  leaseId: string,
  amendmentId: string,
  params: UpdateAmendmentParams
): Promise<LeaseAmendmentResponse> {
  const endpoint = `/landlord/api/leases/${leaseId}/amendments/${amendmentId}`;
  const options: RequestInit = { method: 'PUT', body: JSON.stringify(params) };
  return withValidationErrors(endpoint, options, () => apiFetch<LeaseAmendmentResponse>(endpoint, options));
}

/**
 * Assemble the staged changes, render the amendment PDF, and create a signing envelope (DC-20).
 */
export function generateAmendment(
  leaseId: string,
  amendmentId: string
): Promise<LeaseAmendmentResponse> {
  const endpoint = `/landlord/api/leases/${leaseId}/amendments/${amendmentId}/generate`;
  const options: RequestInit = { method: 'POST' };
  return withValidationErrors(endpoint, options, () => apiFetch<LeaseAmendmentResponse>(endpoint, options));
}

/**
 * Send a GENERATED amendment's signing envelope for signing (DC-21).
 */
export function sendAmendmentForSigning(
  leaseId: string,
  amendmentId: string
): Promise<LeaseAmendmentResponse> {
  return apiFetch<LeaseAmendmentResponse>(
    `/landlord/api/leases/${leaseId}/amendments/${amendmentId}/send`,
    { method: 'POST' }
  );
}

/**
 * Void an amendment that has not yet been signed (DC-22).
 */
export function voidAmendment(
  leaseId: string,
  amendmentId: string
): Promise<LeaseAmendmentResponse> {
  return apiFetch<LeaseAmendmentResponse>(
    `/landlord/api/leases/${leaseId}/amendments/${amendmentId}/void`,
    { method: 'POST' }
  );
}

/**
 * Resend the signing invitation to one recipient of a SENT_FOR_SIGNING amendment (BR-26).
 * `recipientId` is the envelope SigningRecipient id. A failed delivery rejects with a 502.
 */
export function resendAmendmentInvitation(
  leaseId: string,
  amendmentId: string,
  recipientId: string
): Promise<ResendResponse> {
  return apiFetch<ResendResponse>(
    `/landlord/api/leases/${leaseId}/amendments/${amendmentId}/recipients/${recipientId}/resend`,
    { method: 'POST' }
  );
}
